/**
 * 429 handling shared by the pollers and the thunks they call: the server's
 * `Retry-After` hint and a marker error the poll loop recognises.
 */

/** A poll request was answered 429; `retryAfterMs` is the server's hint, if any. */
export class PollRateLimited extends Error {
  readonly retryAfterMs: number | null;

  constructor(retryAfterMs: number | null = null) {
    super('Rate limited');
    this.name = 'PollRateLimited';
    this.retryAfterMs = retryAfterMs;
  }
}

/** `Retry-After` as milliseconds: delta-seconds or an HTTP date. Null when absent or invalid. */
export function parseRetryAfterMs(
  value: unknown,
  now: number = Date.now()
): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.round(value * 1000);
  }
  if (typeof value !== 'string' || value.trim() === '') return null;
  const text = value.trim();
  if (/^\d+(\.\d+)?$/.test(text)) return Math.round(Number(text) * 1000);
  const date = Date.parse(text);
  return Number.isFinite(date) ? Math.max(0, date - now) : null;
}

/** The 429 hidden in an Axios-style failure, or null when it is anything else. */
export function rateLimitFromError(error: unknown): PollRateLimited | null {
  if (error instanceof PollRateLimited) return error;
  const response = (
    error as {
      response?: { status?: number; headers?: Record<string, unknown> };
    } | null
  )?.response;
  if (response?.status !== 429) return null;
  const headers = response.headers ?? {};
  return new PollRateLimited(
    parseRetryAfterMs(headers['retry-after'] ?? headers['Retry-After'])
  );
}

interface RejectedLike {
  meta?: {
    requestStatus?: string;
    rateLimit?: { retryAfterMs: number | null };
  };
  payload?: { status?: number | null; retryAfterMs?: number | null } | string;
}

/**
 * For pollers that dispatch a thunk (which never throws): turns a rejected
 * action that was a 429 into the `PollRateLimited` the poll loop backs off on.
 * Thunks mark it either with `meta.rateLimit` or a payload carrying
 * `{ status: 429, retryAfterMs }`.
 */
export function throwIfRateLimited(action: unknown): void {
  const rejected = action as RejectedLike | null;
  if (rejected?.meta?.rateLimit) {
    throw new PollRateLimited(rejected.meta.rateLimit.retryAfterMs ?? null);
  }
  const payload = rejected?.payload;
  if (
    rejected?.meta?.requestStatus === 'rejected' &&
    typeof payload === 'object' &&
    payload !== null &&
    payload.status === 429
  ) {
    throw new PollRateLimited(payload.retryAfterMs ?? null);
  }
}
